import _ from 'lodash'
import { undoable, withBi } from '../decorators'
import { EVENTS } from '../../../constants/bi'
import { ROLE_MESSAGE, ROLE_SUBMIT_BUTTON, ROLE_TITLE } from '../../../constants/roles'
import {
  DEFAULT_FIELD_MARGIN,
  FORM_PADDING,
  PADDING_FROM_TITLE,
  TOP_PADDING,
  FieldsLayout,
  getFieldsLayout,
} from './constants/layout-settings'
import { EXTRA_COLSPANS } from './constants/columns'
import CoreApi from '../core-api'

import { shiftListItems } from '../../../utils/utils'
import {
  FieldPropHandler,
  getTextAlignmentProp,
  getLabelMarginProp,
  getLabelPaddingProp,
  getInputTextPaddingProp,
  getUpdatesForComplexField,
  orderFieldsByRowsAndCols,
} from './utils'
import { ResponsiveLayout, Connection, ComponentDefinition } from '@wix/platform-editor-sdk'
import { isNavigationButton } from '../steps/utils'
import { SPACE_BETWEEN_FIELDS } from '../fields/api'
import Experiments from '@wix/wix-experiments'
import { getPrimaryConnection } from '../utils'
import { fieldsStore } from '../preset/fields/fields-store'
import { getFieldRenderConfigFields } from '../preset/fields/field-types-data'
import { getContainerType, getGridLayouts, isGridRowEmpty } from './responsive-utils'

export default class LayoutApi {
  private biLogger: any
  private boundEditorSDK: BoundEditorSDK
  private coreApi: CoreApi
  private experiments: Experiments
  public deletedFieldGridLayout: { gridArea: GridArea; breakpointId: string }[] | null

  constructor(boundEditorSDK, coreApi: CoreApi, { biLogger, experiments }) {
    this.boundEditorSDK = boundEditorSDK
    this.coreApi = coreApi
    this.biLogger = biLogger
    this.experiments = experiments
    this.deletedFieldGridLayout = null
  }

  public updateFieldLayout(fieldRef: ComponentRef, newLayout) {
    return this.boundEditorSDK.components.layout.update({
      componentRef: fieldRef,
      layout: newLayout,
    })
  }

  @undoable()
  public async updateFieldsTextAlignment(
    componentRef: ComponentRef,
    fields: FormField[],
    alignment,
  ) {
    await this._updateFieldsProp(fields, getTextAlignmentProp, alignment)
    return this.coreApi.setComponentConnection(componentRef, {
      textAlignment: alignment,
    })
  }

  @undoable()
  public async updateFieldsLabelMargin(componentRef: ComponentRef, fields: FormField[], margin) {
    await this._updateFieldsProp(fields, getLabelMarginProp, margin)
    return this.coreApi.setComponentConnection(componentRef, { labelMargin: margin })
  }

  @undoable()
  public async updateFieldsLabelPadding(componentRef: ComponentRef, fields: FormField[], value) {
    await this._updateFieldsProp(fields, getLabelPaddingProp, value)
    return this.coreApi.setComponentConnection(componentRef, { labelPadding: value })
  }

  @undoable()
  public async updateFieldsInputTextPadding(
    componentRef: ComponentRef,
    fields: FormField[],
    value,
  ) {
    await this._updateFieldsProp(fields, getInputTextPaddingProp, value)
    return this.coreApi.setComponentConnection(componentRef, { textPadding: value })
  }

  private async _updateFieldsProp(
    fields: FormField[],
    getPropHandler: FieldPropHandler,
    value: PROPERTIES,
  ) {
    const fieldsUpdates = []

    const handleFieldPropUpdate = (field: FormField) => {
      const { componentRef, fieldType } = field
      const fieldUpdateProp = getPropHandler(fieldType, value)
      if (!fieldUpdateProp) {
        return
      }

      fieldsUpdates.push(
        this.boundEditorSDK.components.properties.update({
          componentRef,
          props: fieldUpdateProp,
        }),
      )
    }

    fields.forEach((field) => {
      handleFieldPropUpdate(field)

      if (field.childFields.length) {
        field.childFields.forEach((childField) => {
          handleFieldPropUpdate(childField)
        })
      }
    })

    return Promise.all(fieldsUpdates)
  }

  private async _updateFieldsTitles(
    componentRef: ComponentRef,
    fields: FormField[],
    showFieldsTitle: boolean,
  ): Promise<void> {
    const allFields = _.flatMap(
      fields.map((field) => (field.childFields.length ? field.childFields : field)),
    )
    const fieldsUpdates: Promise<any>[] = allFields
      .filter((field) => fieldsStore.get(field.fieldType).supportsLabel)
      .map((field) =>
        this.boundEditorSDK.components.data.update({
          componentRef: field.componentRef,
          data: { label: showFieldsTitle ? field.label : '' },
        }),
      )

    const updateConnection = this.coreApi.setComponentConnection(componentRef, {
      showFieldsTitle,
    })

    await Promise.all([...fieldsUpdates, updateConnection])
  }

  public async showFieldsTitle(componentRef: ComponentRef, fields: FormField[]): Promise<void> {
    await this._updateFieldsTitles(componentRef, fields, true)
  }

  public async hideFieldsTitle(componentRef: ComponentRef, fields: FormField[]) {
    await this._updateFieldsTitles(componentRef, fields, false)
  }

  private async _getButtonAndMessageLayouts(formRef: ComponentRef) {
    const layouts: FormField[] = await this.getChildrenLayouts(formRef, [
      ROLE_SUBMIT_BUTTON,
      ROLE_MESSAGE,
    ])
    return {
      submitButtonLayout: layouts.find((field) => field.role === ROLE_SUBMIT_BUTTON),
      messageLayout: layouts.find((field) => field.role === ROLE_MESSAGE),
    }
  }

  @withBi({ startEvid: EVENTS.PANELS.adiEditFormPanel.DRAG_FIELD_COMPLETE })
  public reorderFormFieldsADI(
    componentRef: ComponentRef,
    { fieldsIndex = null, showTitles = false } = {},
    _biData = {},
  ) {
    return this._updateFieldsLayout(componentRef, 1, {
      isAdiLayout: true,
      fieldsIndex,
      showTitles,
    })
  }

  public updateFieldsLayoutADI(
    componentRef: ComponentRef,
    {
      fieldsIndex = null,
      showTitles = false,
      inputFields = null,
      messageAndButtonLayout = null,
    } = {},
  ) {
    return this._updateFieldsLayout(componentRef, 1, {
      isAdiLayout: true,
      fieldsIndex,
      showTitles,
      inputFields,
      messageAndButtonLayout,
    })
  }

  private _getCruicalElementHeight(elementLayout, padding) {
    if (!elementLayout) {
      return 0
    }
    return elementLayout.height + padding
  }

  @undoable()
  @withBi({
    startEvid: EVENTS.PANELS.formLayoutPanel.CHANGE_LAYOUT,
    endEvid: EVENTS.PANELS.fieldSettingsPanel.VALUE_UPDATED,
  })
  public updateFieldsLayout(componentRef: ComponentRef, columnsNumber: number, _biData = {}) {
    this._updateFieldsLayout(componentRef, columnsNumber, {})
  }

  @undoable()
  public async updateFieldsLayoutByCustomSpacing({
    componentRef,
    formLayout,
    rowSpacing,
    colSpacing,
    showFieldsTitle,
  }) {
    await this._updateFieldsLayout(componentRef, formLayout, {
      rowSpacing,
      colSpacing,
      showTitles: showFieldsTitle,
    })
    return this.coreApi.setComponentConnection(componentRef, {
      spaceBetweenRows: rowSpacing,
      spaceBetweenCols: colSpacing,
    })
  }

  private async _updateFieldsLayout(
    componentRef: ComponentRef,
    columnsNumber: number,
    {
      isAdiLayout = false,
      fieldsIndex = null,
      showTitles = false,
      rowSpacing = 0,
      colSpacing = null,
      inputFields = null,
      messageAndButtonLayout = null,
    } = {},
  ): Promise<void> {
    let fields = inputFields || (await this.coreApi.fields.getFieldsSortByXY(componentRef))

    if (fieldsIndex) {
      const { removedIndex, addedIndex } = fieldsIndex
      fields = shiftListItems(_.cloneDeep(fields), removedIndex, addedIndex)
    }
    const fieldsLayout = getFieldsLayout(isAdiLayout, showTitles, rowSpacing, colSpacing)

    const titleHeight = await this.updateComponentByTitle(componentRef)
    const layoutCalc = await this._calcUpdateFields(componentRef, fields, {
      columnsNumber,
      titleHeight,
      fieldsLayout,
      colSpacing,
    })
    const { height: oldHeight } = await this.boundEditorSDK.components.layout.get({
      componentRef,
    })
    const { submitButtonLayout, messageLayout } =
      messageAndButtonLayout || (await this._getButtonAndMessageLayouts(componentRef))

    const newHeight =
      layoutCalc.y +
      layoutCalc.maxHeight +
      fieldsLayout.lastFieldPadding +
      this._getCruicalElementHeight(submitButtonLayout, fieldsLayout.rolePadding.SUBMIT) +
      this._getCruicalElementHeight(messageLayout, fieldsLayout.rolePadding.MESSAGE)

    if (oldHeight < newHeight) {
      await this.coreApi.addHeightToContainers(componentRef, newHeight - oldHeight)
    }
    const { updates } = layoutCalc

    const shrinkContainersUpdates = _.filter(updates, ({ shrinkContainer }) => shrinkContainer)
    const expandContainersUpdates = _.filter(updates, ({ expandContainer }) => expandContainer)
    const nonContainersUpdates = _.difference(updates, [
      ...expandContainersUpdates,
      ...shrinkContainersUpdates,
    ])

    const layoutUpdater = (data) =>
      this.boundEditorSDK.components.layout.update({
        componentRef: data.componentRef,
        layout: data.layout,
      })

    // must finished before update fields
    await Promise.all(expandContainersUpdates.map(layoutUpdater))

    await Promise.all([
      ...nonContainersUpdates.map(layoutUpdater),
      ...this._updateSubmitAndMessage(submitButtonLayout, messageLayout, layoutCalc, fieldsLayout),
    ])
    // must start after fields updated
    await Promise.all(shrinkContainersUpdates.map(layoutUpdater))

    if (oldHeight > newHeight) {
      await this.coreApi.addHeightToContainers(componentRef, newHeight - oldHeight)
    }

    await this.coreApi.setComponentConnection(componentRef, {
      columns: columnsNumber,
    })
  }

  public async updateComponentByTitle(componentRef: ComponentRef): Promise<number> {
    const titleComponentRef = await this.coreApi.findComponentByRole(componentRef, ROLE_TITLE)
    if (!titleComponentRef) {
      return 0
    }

    const { height } = await this.boundEditorSDK.components.layout.get({
      componentRef: titleComponentRef,
    })
    await this.boundEditorSDK.components.layout.update({
      componentRef: titleComponentRef,
      layout: { x: FORM_PADDING, y: TOP_PADDING },
    })

    return height
  }

  public async getChildrenLayouts(
    componentRef: ComponentRef,
    childRoles: string[] | string,
    withoutFilter = false,
  ): Promise<any[]> {
    if (_.isString(childRoles)) {
      childRoles = [childRoles as string]
    }
    const pred = (role) => _.includes(childRoles, role)

    const childComps = await this.boundEditorSDK.components.getChildren({
      componentRef,
    })
    const childrenLayouts = await Promise.all(
      childComps.map(async (child) => {
        const { role } = await this.coreApi.getComponentConnection(child)
        if (withoutFilter || pred(role)) {
          const layout = await this.boundEditorSDK.components.layout.get({
            componentRef: child,
          })
          return { componentRef: child, role, ...layout }
        }
        return null
      }),
    )
    return _.filter(childrenLayouts, (x) => !!x)
  }

  private _updateSubmitAndMessage(submitLayout, messageLayout, { y, maxHeight }, FIELDS_LAYOUT) {
    let submitPadding = 0
    const updates = []
    if (submitLayout) {
      submitPadding = submitLayout.height + FIELDS_LAYOUT.rolePadding.SUBMIT
      updates.push(
        this.boundEditorSDK.components.layout.update({
          componentRef: submitLayout.componentRef,
          layout: { y: y + maxHeight + FIELDS_LAYOUT.rolePadding.SUBMIT },
        }),
      )
    }
    if (messageLayout) {
      updates.push(
        this.boundEditorSDK.components.layout.update({
          componentRef: messageLayout.componentRef,
          layout: {
            y: y + maxHeight + FIELDS_LAYOUT.rolePadding.MESSAGE + submitPadding,
          },
        }),
      )
    }
    return updates
  }

  public updateYWithPadding(componentRef: ComponentRef, padding, { y, maxHeight }, key = 'y') {
    return this.boundEditorSDK.components.layout.update({
      componentRef,
      layout: { [key]: y + maxHeight + padding },
    })
  }

  public async alignChildComponentToCenter({ containerRef, childRef }) {
    const [containerLayout, childLayout] = await Promise.all([
      this.boundEditorSDK.components.layout.get({
        componentRef: containerRef,
      }),
      this.boundEditorSDK.components.layout.get({
        componentRef: childRef,
      }),
    ])

    const newY = (containerLayout.height - childLayout.height) / 2
    const newX = (containerLayout.width - childLayout.width) / 2

    return this.boundEditorSDK.components.layout.update({
      componentRef: childRef,
      layout: {
        x: _.max([0, newX]),
        y: _.max([0, newY]),
      },
    })
  }

  public async centerComponentInsideLightbox(componentRef: ComponentRef) {
    const { width, height, x, y } = await this.boundEditorSDK.components.layout.get({
      componentRef,
    })

    return this.boundEditorSDK.components.layout.update({
      componentRef,
      layout: {
        x: _.max([0, x - width / 2]),
        y: _.max([0, y - height / 2]),
      },
    })
  }

  private async _handleComplexFieldRelayoutUpdates(
    complexField: any,
    widgetUpdate: { componentRef: any; layout: FieldLayout },
    reduceFunc: (
      prevFieldLayout: any,
      field: any,
    ) => Promise<{
      updates: any[]
      startX: any
      x: any
      y: any
      width: number
      defaultWidth: any
      maxHeight: any
      currentCol: any
    }>,
    width: number,
    defaultWidth: any,
    colSize: number,
  ) {
    let fieldHeightAfterUpdate = complexField.height
    let updates

    const config = getFieldRenderConfigFields([], complexField.fieldType)

    if (config.layout) {
      updates = await this.calcComplexFieldUpdatesByDefinedRowAndCol(complexField, widgetUpdate)
    } else {
      const { complexFieldHeight, updates: allComplexUpdates } = await getUpdatesForComplexField(
        complexField,
        widgetUpdate,
        reduceFunc,
        {
          width,
          defaultWidth,
          colSize,
        },
      )
      fieldHeightAfterUpdate = complexFieldHeight
      updates = allComplexUpdates
    }

    return {
      updates,
      newHeight: fieldHeightAfterUpdate,
    }
  }

  public async calcComplexFieldUpdatesByDefinedRowAndCol(
    field: FormField,
    widgetFieldUpdate: { componentRef: ComponentRef; layout: FieldLayout },
  ): Promise<{ componentRef: ComponentRef; layout: FieldLayout }[]> {
    const [innerContainer] = await this.boundEditorSDK.components.getChildren({
      componentRef: field.componentRef,
    })
    const { layout: containerUpdate } = widgetFieldUpdate
    const innerContainerUpdate = {
      componentRef: innerContainer,
      layout: {
        width: containerUpdate.width || field.width,
        height: containerUpdate.height || field.height,
      },
    }
    const fieldUpdates = orderFieldsByRowsAndCols(field, containerUpdate)

    let updates = [...fieldUpdates, innerContainerUpdate]

    if (field.width < containerUpdate.width) {
      updates = [widgetFieldUpdate, ...updates.reverse()]
    } else {
      updates = [...updates, widgetFieldUpdate]
    }
    return updates
  }

  private _calcUpdateFields(
    componentRef: ComponentRef,
    fields: FormField[],
    {
      columnsNumber,
      titleHeight = 0,
      fieldsLayout,
      colSpacing,
    }: {
      columnsNumber: number
      titleHeight: number
      fieldsLayout: FieldsLayout
      colSpacing?: number
    },
  ) {
    const fieldMargin = colSpacing !== null ? colSpacing : DEFAULT_FIELD_MARGIN
    const reduceFunc = async (prevFieldLayout, field) => {
      const {
        updates,
        startX,
        x,
        y,
        maxHeight,
        defaultWidth,
        currentCol,
        firstInContainer,
      } = await prevFieldLayout
      const colSize = await this._getFieldColSize(field.componentRef, columnsNumber)
      const width = colSize * defaultWidth + (colSize - 1) * fieldMargin
      let nextCol = currentCol + colSize
      let layout = { width, x: x + width + fieldMargin, y }
      let overrideMaxHeight = -1
      if (nextCol > columnsNumber) {
        const extraHeight = firstInContainer ? 0 : fieldsLayout.heightPadding
        nextCol = colSize
        layout = { width, x: startX, y: y + maxHeight + extraHeight }
        overrideMaxHeight = 0
      }

      const update = {
        componentRef: field.componentRef,
        layout,
      }

      let newUpdates = [update]
      let fieldNewHeight = field.height

      if (field.childFields.length) {
        const {
          updates: complexUpdates,
          newHeight,
        } = await this._handleComplexFieldRelayoutUpdates(
          field,
          update,
          reduceFunc,
          width,
          defaultWidth,
          colSize,
        )
        fieldNewHeight = newHeight
        newUpdates = complexUpdates
      }

      return {
        updates: [...updates, ...newUpdates],
        startX,
        x: layout.x,
        y: layout.y,
        width,
        defaultWidth,
        maxHeight: _.max([overrideMaxHeight > -1 ? overrideMaxHeight : maxHeight, fieldNewHeight]),
        currentCol: nextCol,
      }
    }

    const getBoxConfig = async (compRef: ComponentRef, columns: number, titleHeightPx: number) => {
      const { width } = await this.boundEditorSDK.components.layout.get({
        componentRef: compRef,
      })
      const titleExtraHeight = titleHeightPx > 0 ? titleHeightPx + PADDING_FROM_TITLE : 0

      return {
        updates: [],
        startX: fieldsLayout.startX,
        currentCol: columns,
        x: fieldsLayout.formPadding,
        y: 0,
        width: 0,
        maxHeight: fieldsLayout.formPadding + titleExtraHeight - DEFAULT_FIELD_MARGIN / 2,
        defaultWidth: fieldsLayout.defaultWidth(width, columns),
        firstFieldPerXAxis: {},
      }
    }

    return _.reduce(fields, reduceFunc, getBoxConfig(componentRef, columnsNumber, titleHeight))
  }

  private async _getFieldColSize(componentRef: ComponentRef, columns: number): Promise<number> {
    const type = await this.boundEditorSDK.components.getType({ componentRef })
    return _.min([EXTRA_COLSPANS[type] || 0, columns - 1]) + 1
  }

  public async getChildrenResponsiveLayouts(
    containerRef: ComponentRef,
  ): Promise<{
    itemLayoutType: ResponsiveItemLayoutType
    layouts: {
      layoutResponsive: ResponsiveLayout
      componentRef: ComponentRef
      role: string | undefined
    }[]
  }> {
    const [children, containerLayout] = await Promise.all([
      this.boundEditorSDK.components.getChildren({
        componentRef: containerRef,
      }),
      this.boundEditorSDK.components.get({
        componentRefs: [containerRef],
        properties: ['layoutResponsive'],
      }),
    ])

    const containerLayoutType: ResponsiveContainerLayoutType = getContainerType(
      _.get(containerLayout, '[0].layoutResponsive'),
    )

    let sortByKey = ''
    let itemLayoutType: ResponsiveItemLayoutType

    switch (containerLayoutType) {
      case 'GridContainerLayout':
        sortByKey = 'gridArea.rowEnd'
        itemLayoutType = 'GridItemLayout'
        break
      default:
        sortByKey = 'order'
        itemLayoutType = 'StackItemLayout'
    }

    const convertLayouts = (item: {
      componentRef: ComponentRef
      layoutResponsive: ResponsiveLayout
      connections: Connection[]
    }) => ({
      componentRef: item.componentRef,
      layoutResponsive: item.layoutResponsive,
      role: _.get(getPrimaryConnection(item.connections), 'role'),
    })

    const layouts = await this.boundEditorSDK.components.get({
      componentRefs: children,
      properties: ['layoutResponsive', 'connections'],
    })

    return {
      itemLayoutType,
      layouts: _.sortBy(
        layouts.map(convertLayouts),
        `layoutResponsive.itemLayouts[0].${sortByKey}`,
      ),
    }
  }

  private async _calcYAxisLimit(
    componentRef: ComponentRef,
    fieldY: number,
    fieldsData?: FormField[],
  ): Promise<number> {
    const childLayouts = fieldsData
      ? fieldsData
      : await this.getChildrenLayouts(componentRef, null, true)
    const navigationButtons = _.filter(
      childLayouts,
      (element) => isNavigationButton(element.role) && fieldY < element.y + element.height,
    )
    if (!_.isEmpty(navigationButtons)) {
      return _.get(
        _.minBy(navigationButtons, (field: any) => field.y),
        'y',
      )
    } else {
      const { height, y } = await this.boundEditorSDK.components.layout.get({
        componentRef,
      })
      return height + y - SPACE_BETWEEN_FIELDS
    }
  }

  public async loadInitialPanelData({ componentRef }) {
    const [fields, appConfig] = await Promise.all([
      this.coreApi.fields.getFieldsSortByXY(componentRef),
      this.coreApi.fetchAppConfig({ formComponentRef: componentRef }),
    ])

    return {
      fields,
      allFieldsWithTitles: !_.some(fields, (field: FormField) => !field.showLabel),
      appConfig,
    }
  }

  public async addHeightToContainerIfFieldCrossedLimit(
    componentRef: ComponentRef,
    totalHeightToAdd: number,
    newFieldY: number,
    fieldsData?: FormField[],
  ) {
    const yAxisLimit = await this._calcYAxisLimit(componentRef, newFieldY, fieldsData)
    const shouldAddHeightToContainer = totalHeightToAdd + newFieldY >= yAxisLimit

    if (shouldAddHeightToContainer) {
      await this.coreApi.addHeightToContainers(componentRef, totalHeightToAdd)
    }

    return shouldAddHeightToContainer
  }

  public async handleFieldInGridDeleted(formRef: ComponentRef) {
    if (!this.deletedFieldGridLayout) {
      return
    }

    const childrenPositions = await this._getChildrenPositionsInGrid(formRef)

    const rowsToDelete: {
      breakpointId: string
      index: number
    }[] = this.deletedFieldGridLayout.reduce(
      (acc, { breakpointId, gridArea: { rowStart, rowEnd } }) => {
        const childrenPositionsCurrentBreakpoint = childrenPositions
          .map((positions) => positions.find((pos) => pos.breakpointId === breakpointId))
          .filter((position) => position)
        const rowsToDeleteCurrentBreakpoint = []
        for (let i = rowStart; i < rowEnd; i++) {
          if (isGridRowEmpty(childrenPositionsCurrentBreakpoint, i)) {
            rowsToDeleteCurrentBreakpoint.push({ breakpointId, index: i - 1 })
          }
        }
        return [...acc, ...rowsToDeleteCurrentBreakpoint]
      },
      [],
    )

    await this._removeGridRows(formRef, rowsToDelete)
    this.deletedFieldGridLayout = null
  }

  private async _getChildrenPositionsInGrid(formRef: ComponentRef) {
    return Promise.all(
      (
        await this.boundEditorSDK.components.getChildren({
          componentRef: formRef,
        })
      ).map((field) =>
        this.boundEditorSDK.responsive.grid.getChildPositions({
          componentRef: field,
        }),
      ),
    )
  }

  private _removeGridRows(
    formRef: ComponentRef,
    rowsToDelete: {
      breakpointId: string
      index: number
    }[],
  ) {
    if (!rowsToDelete.length) {
      return
    }
    return Promise.all(
      rowsToDelete.map(({ breakpointId, index }) =>
        this.boundEditorSDK.responsive.grid.removeRow({
          index,
          componentRef: formRef,
          breakpointId,
        }),
      ),
    )
  }

  public beforeComponentRemoved(componentDefinition: ComponentDefinition) {
    if (!this.coreApi.isResponsive()) {
      return
    }
    const deletedFieldLayout = getGridLayouts(componentDefinition)
    this.deletedFieldGridLayout = deletedFieldLayout.length ? deletedFieldLayout : null
  }
}
